import { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, X, Loader2, CheckCircle, AlertCircle, Package, MapPin, Euro, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import cashxDeliveryService from '@/services/cashxDeliveryService';
import DeliveryDetailView from '@/components/DeliveryDetailView';
import jsQR from 'jsqr';

const QRScanner = ({ onScanSuccess, onClose, title, description }) => {
  const { t } = useTranslation();
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false);
  const [validatedOrder, setValidatedOrder] = useState(null);
  const [claimedDeliveryId, setClaimedDeliveryId] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  
  // Default values with translations
  const displayTitle = title || t('qr.title');
  const displayDescription = description || t('qr.scanOrderCode');

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera on mobile
      });

      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setScanning(true);
      
      // Start scanning for QR codes
      startQRDetection();

      toast.success(t('qr.cameraActive'), {
        description: t('qr.holdToCamera')
      });
    } catch (error) {
      console.error('Camera Error:', error);
      toast.error(t('qr.cameraError'), {
        description: t('qr.enableInSettings')
      });
    }
  };

  const stopScanning = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setScanning(false);
  };

  const startQRDetection = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video) return;
    
    const context = canvas.getContext('2d');
    
    scanIntervalRef.current = setInterval(() => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        // Set canvas size to match video
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        
        // Draw video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Get image data
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        // Scan for QR code
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        });
        
        if (code) {
          console.log('QR Code detected:', code.data);
          handleQRDetected(code.data);
        }
      }
    }, 300); // Scan every 300ms
  };

  const handleManualInput = () => {
    const testToken = prompt('Test: QR-Token eingeben (JWT)');
    if (testToken) {
      // For shift mode, just pass token back, no claiming
      if (title && title.includes('Schicht')) {
        onScanSuccess(testToken);
      } else {
        handleQRDetected(testToken);
      }
    }
  };

  const handleQRDetected = async (qrToken) => {
    if (processing) return;

    // For shift mode, just pass token back
    if (title && title.includes('Schicht')) {
      onScanSuccess(qrToken);
      return;
    }

    setProcessing(true);
    stopScanning();

    try {
      // First, validate the QR token with Cash-X
      const validationResult = await cashxDeliveryService.validateQR(qrToken);

      if (validationResult.valid) {
        // Show order details and let user confirm claiming
        setValidatedOrder({
          ...validationResult.order_details,
          order_id: validationResult.order_id,
          qr_token: qrToken
        });
        setShowConfirmation(true);
      } else {
        toast.error(t('qr.claimError'), {
          description: validationResult.error || 'QR-Code ungültig'
        });
        setProcessing(false);
      }
    } catch (error) {
      console.error('QR Scan Error:', error);
      toast.error('Fehler beim Scannen', {
        description: error.message
      });
      setProcessing(false);
    }
  };

  const handleConfirmClaim = async () => {
    if (!validatedOrder) return;
    
    try {
      const claimResult = await cashxDeliveryService.claimDelivery(validatedOrder.order_id);
      
      if (claimResult.success) {
        toast.success(t('qr.orderClaimed'), {
          description: `Bestellung #${validatedOrder.order_number || validatedOrder.order_id}`
        });
        
        // Store delivery ID to show details
        const deliveryId = claimResult.delivery_id || claimResult.delivery?.delivery_id;
        if (deliveryId) {
          setClaimedDeliveryId(deliveryId);
          setShowConfirmation(false);
          setShowDeliveryDetails(true); // Show delivery details after claim
        } else {
          // No delivery ID, just call success callback
          if (onScanSuccess) {
            onScanSuccess(claimResult.order || validatedOrder);
          }
          setShowConfirmation(false);
          setValidatedOrder(null);
        }
      } else {
        toast.error(t('qr.claimError'), {
          description: claimResult.error
        });
      }
    } catch (error) {
      toast.error('Übernahme fehlgeschlagen', {
        description: error.message
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelClaim = () => {
    setShowConfirmation(false);
    setValidatedOrder(null);
    setProcessing(false);
  };

  return (
    <>
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">{displayTitle}</h3>
          {onClose && (
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-zinc-400"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Order Confirmation View */}
        {showConfirmation && validatedOrder && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-500 mb-4">
              <CheckCircle className="h-6 w-6" />
              <span className="font-bold">QR-Code gültig!</span>
            </div>
            
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800 space-y-3">
              {/* Order Header */}
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-primary" />
                    <span className="font-bold text-white text-lg">
                      #{validatedOrder.order_number || validatedOrder.order_id}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xl font-bold text-white">
                  <Euro className="h-5 w-5" />
                  {(validatedOrder.total_gross || validatedOrder.total || 0).toFixed(2)}
                </div>
              </div>

              {/* Customer Info */}
              <div className="space-y-2 pt-2 border-t border-zinc-800">
                <div className="flex items-center gap-2 text-white">
                  <User className="h-4 w-4 text-primary" />
                  <span>{validatedOrder.customer_name || 'Kunde'}</span>
                </div>
                
                {validatedOrder.customer_phone && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Phone className="h-4 w-4 text-primary" />
                    <a href={`tel:${validatedOrder.customer_phone}`} className="hover:text-white">
                      {validatedOrder.customer_phone}
                    </a>
                  </div>
                )}

                {validatedOrder.delivery_address && (
                  <div className="flex items-start gap-2 text-zinc-400">
                    <MapPin className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p>{validatedOrder.delivery_address.street} {validatedOrder.delivery_address.house_number}</p>
                      <p>{validatedOrder.delivery_address.postal_code} {validatedOrder.delivery_address.city}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Items */}
              {validatedOrder.items && validatedOrder.items.length > 0 && (
                <div className="pt-2 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Artikel:</p>
                  <div className="text-sm text-zinc-300 space-y-1">
                    {validatedOrder.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{item.qty || item.quantity}x {item.name}</span>
                        <span className="text-zinc-500">{((item.price || 0) * (item.qty || item.quantity || 1)).toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {validatedOrder.notes && (
                <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded p-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-300">{validatedOrder.notes}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleCancelClaim}
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-400"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleConfirmClaim}
                disabled={processing}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Übernehme...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Übernehmen
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Initial Scanner View */}
        {!scanning && !processing && !showConfirmation && (
          <div className="space-y-4">
            <Button
              onClick={startScanning}
              className="w-full h-16 bg-primary hover:bg-primary/90 text-lg"
            >
              <Camera className="h-6 w-6 mr-2" />
              {t('qr.startCamera')}
            </Button>

            <Button
              onClick={handleManualInput}
              variant="outline"
              className="w-full border-zinc-700 text-zinc-400"
            >
              {t('qr.manualInput')}
            </Button>

            <p className="text-xs text-zinc-500 text-center">
              {displayDescription}
            </p>
          </div>
        )}

        {/* Camera Scanning View */}
        {scanning && !showConfirmation && (
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
              />
              <canvas
                ref={canvasRef}
                className="hidden"
              />
              <div className="absolute inset-0 border-4 border-primary/50 rounded-lg pointer-events-none">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-primary rounded-lg"></div>
              </div>
            </div>

            <Button
              onClick={stopScanning}
              variant="outline"
              className="w-full border-red-500 text-red-500"
            >
              {t('common.cancel')}
            </Button>

            <p className="text-sm text-zinc-400 text-center">
              {t('qr.holdInArea')}
            </p>
          </div>
        )}

        {/* Processing View */}
        {processing && !showConfirmation && (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <p className="text-white">{t('qr.processing')}</p>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Delivery Detail View Dialog */}
    {showDeliveryDetails && claimedDeliveryId && (
      <DeliveryDetailView
        open={showDeliveryDetails}
        onClose={() => {
          setShowDeliveryDetails(false);
          setClaimedDeliveryId(null);
          setValidatedOrder(null);
          if (onScanSuccess) {
            onScanSuccess(validatedOrder);
          }
        }}
        deliveryId={claimedDeliveryId}
        orderInfo={validatedOrder}
      />
    )}
    </>
  );
};

export default QRScanner;
